import { estimateJobRunTime } from "../estimateJobRunTime";
import { CollectorConfig } from "providers/HostedScrapingProvider/types";
import { useUser } from "routes/dataroutes/UserData";
import {
  AmazonProjectDetails,
  SidebarConcurrency,
  Credits,
  CostRowHostedScraper,
  EbayProjectDetails,
  GoogleProjectDetails,
  RedfinProjectDetails,
  Separator,
  SummaryRow,
  UrlProjectDetails,
  WalmartProjectDetails,
  HomeDepotProjectDetails,
  HomeDepotProductProjectDetails,
  HomeDepotReviewsProjectDetails,
} from "./ProjectSummaryComponents";
import { isAmazonProject, isAsyncUrlsProject, isEbayProject, isGoogleProject, isWalmartProject, isRedfinProject, isHomeDepotSearchProject, isHomeDepotProductProject, isHomeDepotReviewsProject, isAsyncUrlsLlmProject } from "sdecontent";
import useUserCounters from "hooks/useUserCounters";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { LegacyRateCallout } from "components/LegacyRateCallout";


const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-2 text-sm font-medium uppercase tracking-wide text-neutral-500">
    {children}
  </div>
);

export const JOB_AGGREGATION_SPEED_SECOND_PER_JOB = 0.043;

export type IProjectsummarysidebarProps = {
  details: { title: string, value: string }[];
  cost: number | undefined;
  costInProgress: boolean;
  config: CollectorConfig;
  showSlowJobWarning: boolean;
  inputCount: number | undefined;
  /** Standard price to warn about when any input domain is on a legacy rate (ENG-1112). */
  legacyStandardCredits?: number;
};

export const formatTimeHoursMins = (seconds: number) => {
  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.ceil((seconds % 3600) / 60);
  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ${hours % 24} hr${(hours % 24) !== 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours} hr${hours !== 1 ? 's' : ''} ${String(minutes).padStart(2, '0')} min${minutes !== 1 ? 's' : ''}`;
  } else {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
}

export const preCalculatedRuntimeEstimation = (numberOfInputs: number, remainingConcurrency: number) => {
  if (remainingConcurrency === 0) {
    return "∞";
  }
  if (numberOfInputs === 0) {
    return "-";
  }
  const { remainingTotalSeconds } = estimateJobRunTime(remainingConcurrency, undefined, numberOfInputs, 0, undefined);
  return formatTimeHoursMins(remainingTotalSeconds);
}

export const ProjectSummarySidebarHostedScraper = ({details, config, cost, costInProgress, inputCount, showSlowJobWarning, legacyStandardCredits} : IProjectsummarysidebarProps) => {
  const user = useUser();
  const { activeSubscription } = useActiveSubscription();
  const userCounters = useUserCounters();
  const creditsUsed = userCounters.counters?.requestCount ?? 0;
  const creditsLeft = user ? user.creditsRemaining : 0;

  const concurrencyUsed = userCounters.counters?.concurrentRequests ?? 0;
  const concurrencyLoading = Boolean(userCounters.isLoading);
  const concurrencyLimit = activeSubscription?.apiConcurrencyLimit ?? 0;

  const runsLeft = costInProgress || cost === undefined || cost === 0
    ? '?'
    : Math.floor(creditsLeft / cost);

  return (
    <div className="p-6 h-full bg-lightestGray text-neutral-900">

      <SectionHeader>Cost per job</SectionHeader>
      <CostRowHostedScraper cost={cost} costInProgress={costInProgress} runsLeft={runsLeft} />
      {!costInProgress && <LegacyRateCallout standardCredits={legacyStandardCredits} className="mt-3" />}

      <Separator />

      <SectionHeader>API credits used</SectionHeader>
      <Credits
        creditsLeft={creditsLeft}
        creditsUsed={creditsUsed}
        creditsNeededForThisJob={cost ?? 0}
      />

      <Separator />

      <SectionHeader>Concurrency</SectionHeader>
      <SidebarConcurrency concurrency={concurrencyUsed} concurrencyLimit={concurrencyLimit} concurrencyIsLoading={concurrencyLoading} inputCount={inputCount} showWarning={showSlowJobWarning} />

      <Separator />

      <SectionHeader>Job Summary</SectionHeader>
      {details.map(
        ({title, value}) => <SummaryRow key={value} title={title} value={value} />
      )}

      <Separator />

      <SectionHeader>Options &amp; Filters</SectionHeader>
      { isAsyncUrlsProject(config.type)
        && <UrlProjectDetails collectorConfig={config} />}
      { isAsyncUrlsLlmProject(config.type)
        && <UrlProjectDetails collectorConfig={config} />}
      { isAmazonProject(config.type)
        && <AmazonProjectDetails collectorConfig={config} />}
      { isGoogleProject(config.type)
        && <GoogleProjectDetails collectorConfig={config} />}
      { isWalmartProject(config.type)
        && <WalmartProjectDetails collectorConfig={config} />}
      { isEbayProject(config.type)
        && <EbayProjectDetails collectorConfig={config} />}
      { isRedfinProject(config.type)
        && <RedfinProjectDetails collectorConfig={config} />}
      { isHomeDepotSearchProject(config.type)
        && <HomeDepotProjectDetails collectorConfig={config} />}
      { isHomeDepotProductProject(config.type)
        && <HomeDepotProductProjectDetails collectorConfig={config} />}
      { isHomeDepotReviewsProject(config.type)
        && <HomeDepotReviewsProjectDetails collectorConfig={config} />}

    </div>
  )
}
